import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DownwardSelectOption {
    value: string;
    label: string;
}

interface DownwardSelectProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    options: DownwardSelectOption[];
    id?: string;
}

/**
 * List anchored under the trigger. A native select flips upward when the
 * control sits low in a dialog; this menu stays below the field and scrolls
 * inside itself.
 */
export function DownwardSelect({ value, onChange, placeholder, options, id }: DownwardSelectProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const selected = options.find((option) => option.value === value);

    useEffect(() => {
        if (!open) return;
        const onPointer = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className="relative">
            <button
                id={id}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm text-left flex items-center justify-between gap-2"
                onClick={() => setOpen((current) => !current)}
            >
                <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
                    {selected?.label || placeholder}
                </span>
                <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
            </button>
            {open && (
                <ul
                    role="listbox"
                    aria-labelledby={id}
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded border border-border bg-card shadow-md"
                >
                    {options.map((option) => (
                        <li key={option.value}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={option.value === value}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => {
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                            >
                                {option.label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
